import React, { useState, useEffect } from 'react';

interface HealthReport {
  HealthReportId: string;
  ProjectId: string;
  ReportMonth: string;
  PMEmail: string;
  RAGStatus: 'Green' | 'Amber' | 'Red';
  ProgressSummary: string | null;
  BlockersAndRisks: string | null;
  NextMonthPriorities: string | null;
  SubmittedDate: string;
}

interface HealthReportFormProps {
  projectId: string;
  onSuccess?: () => void;
}

export default function HealthReportForm({ projectId, onSuccess }: HealthReportFormProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [reports, setReports] = useState<HealthReport[]>([]);
  const [formData, setFormData] = useState({
    reportMonth: new Date().toISOString().slice(0, 7),
    ragStatus: 'Green' as 'Green' | 'Amber' | 'Red',
    progressSummary: '',
    blockersAndRisks: '',
    nextMonthPriorities: '',
  });

  useEffect(() => {
    fetchReports();
  }, [projectId]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/health-reports/${projectId}`);
      const data = await res.json();
      if (data.success) {
        setReports(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    setSubmitting(true);
    try {
      const res = await fetch('/health-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          ...formData,
          pmEmail: localStorage.getItem('auth_email') || 'pm@example.com',
        }),
      });

      const data = await res.json();
      if (data.success) {
        setSuccessMessage('Health report submitted successfully!');
        setFormData({
          reportMonth: new Date().toISOString().slice(0, 7),
          ragStatus: 'Green',
          progressSummary: '',
          blockersAndRisks: '',
          nextMonthPriorities: '',
        });
        fetchReports();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setErrorMessage(`Error: ${data.error}`);
      }
    } catch (err: any) {
      setErrorMessage(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {successMessage && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          background: '#d4edda',
          color: '#155724',
          borderRadius: '4px',
          border: '1px solid #c3e6cb',
        }}>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          background: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          border: '1px solid #f5c6cb',
        }}>
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <h2 style={{ marginTop: 0 }}>Monthly Health Report</h2>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Month *</label>
          <input
            type="month"
            value={formData.reportMonth}
            onChange={(e) => setFormData({ ...formData, reportMonth: e.target.value })}
            required
            style={{ width: '100%', padding: '0.5rem', marginBottom: 0 }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Status *</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
            {(['Green', 'Amber', 'Red'] as const).map(status => (
              <label key={status} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="ragStatus"
                  value={status}
                  checked={formData.ragStatus === status}
                  onChange={(e) => setFormData({ ...formData, ragStatus: e.target.value as any })}
                  style={{ marginRight: '0.5rem' }}
                />
                <span style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  background: status === 'Green' ? '#d4edda' : status === 'Amber' ? '#fff3cd' : '#f8d7da',
                  color: status === 'Green' ? '#155724' : status === 'Amber' ? '#856404' : '#721c24',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}>
                  {status}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Progress Summary</label>
          <textarea
            value={formData.progressSummary}
            onChange={(e) => setFormData({ ...formData, progressSummary: e.target.value })}
            placeholder="What was accomplished this month?"
            style={{ width: '100%', padding: '0.5rem', minHeight: '80px' }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Blockers & Risks</label>
          <textarea
            value={formData.blockersAndRisks}
            onChange={(e) => setFormData({ ...formData, blockersAndRisks: e.target.value })}
            placeholder="Any blockers or risks to address?"
            style={{ width: '100%', padding: '0.5rem', minHeight: '80px' }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Next Month Priorities</label>
          <textarea
            value={formData.nextMonthPriorities}
            onChange={(e) => setFormData({ ...formData, nextMonthPriorities: e.target.value })}
            placeholder="What are the priorities for next month?"
            style={{ width: '100%', padding: '0.5rem', minHeight: '80px' }}
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </form>

      {reports.length > 0 && (
        <div>
          <h3>Report History</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {reports.map(report => (
              <div key={report.HealthReportId} style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '2rem', marginBottom: '1rem' }}>
                  <div>
                    <strong>Month:</strong> {report.ReportMonth}
                  </div>
                  <div>
                    <strong>Status:</strong> <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '3px',
                      background: report.RAGStatus === 'Green' ? '#d4edda' : report.RAGStatus === 'Amber' ? '#fff3cd' : '#f8d7da',
                      color: report.RAGStatus === 'Green' ? '#155724' : report.RAGStatus === 'Amber' ? '#856404' : '#721c24',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                    }}>
                      {report.RAGStatus}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {new Date(report.SubmittedDate).toLocaleDateString()}
                  </div>
                </div>

                {report.ProgressSummary && (
                  <div style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #eee' }}>
                    <strong>Progress:</strong> {report.ProgressSummary}
                  </div>
                )}
                {report.BlockersAndRisks && (
                  <div style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #eee' }}>
                    <strong>Blockers:</strong> {report.BlockersAndRisks}
                  </div>
                )}
                {report.NextMonthPriorities && (
                  <div>
                    <strong>Next Month:</strong> {report.NextMonthPriorities}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
